import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Wallet,
  DollarSign,
  Calendar,
  Download,
  CheckCircle2,
  Clock,
  FileText,
  Eye,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useT, useLang } from '@/i18n';

const PayoutsPage = () => {
  const t = useT();
  const [lang] = useLang();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(searchParams.get('owner') || '');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const isOwner = user?.role === 'OWNER';
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    if (!isOwner) {
      fetchOwners();
    }
    fetchInvoices();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [selectedOwner, statusFilter]);

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API}/owners`);
      setOwners(response.data.items || []);
    } catch (error) {
      console.error('Failed to load owners');
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedOwner && !isOwner) params.owner_id = selectedOwner;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await axios.get(`${API}/owner-invoices`, { params });
      setInvoices(response.data.items || []);
    } catch (error) {
      toast.error(t('payouts_page.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/owner-invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t('payouts_page.invoice_downloaded'));
    } catch (error) {
      toast.error(t('payouts_page.download_failed'));
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/owner-invoices/${invoiceId}/status`, {
        status: newStatus
      });
      const labelKey = newStatus === 'PAID' ? 'payouts_page.status_paid'
                     : newStatus === 'PAID_OUT' ? 'payouts_page.status_paid_out'
                     : 'payouts_page.status_pending';
      toast.success(t('payouts_page.marked_as', { status: t(labelKey) }));
      fetchInvoices();
    } catch (error) {
      toast.error(t('payouts_page.update_status_failed'));
    }
  };

  const _localeMap = { en: 'en-GB', es: 'es-ES', fi: 'fi-FI', sv: 'sv-SE', de: 'de-DE' };
  const _loc = _localeMap[lang] || 'en-GB';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(_loc, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'GENERATED':
        return <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">{t('payouts_page.status_pending')}</Badge>;
      case 'PAID':
        return <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">{t('payouts_page.status_paid')}</Badge>;
      case 'PAID_OUT':
        return <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">{t('payouts_page.status_paid_out')}</Badge>;
      case 'CANCELLED':
        return <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">{t('payouts_page.status_cancelled')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Calculate summary stats
  const stats = {
    total: invoices.reduce((sum, inv) => sum + (inv.total_payout || 0), 0),
    pending: invoices.filter(inv => inv.status === 'GENERATED').reduce((sum, inv) => sum + (inv.total_payout || 0), 0),
    paid: invoices.filter(inv => inv.status === 'PAID').reduce((sum, inv) => sum + (inv.total_payout || 0), 0),
    paidOut: invoices.filter(inv => inv.status === 'PAID_OUT').reduce((sum, inv) => sum + (inv.total_payout || 0), 0),
    count: invoices.length
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payouts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
            {isOwner ? t('payouts_page.title_owner') : t('payouts_page.title_admin')}
          </h1>
          <p className="text-[#194a50]/70 mt-1">
            {isOwner ? t('payouts_page.subtitle_owner') : t('payouts_page.subtitle_admin')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-sm border-[#194a50]/5">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {!isOwner && (
              <Select value={selectedOwner || "all"} onValueChange={(val) => setSelectedOwner(val === "all" ? "" : val)}>
                <SelectTrigger className="w-64" data-testid="owner-select">
                  <SelectValue placeholder={t('payouts_page.all_owners')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('payouts_page.all_owners')}</SelectItem>
                  {owners.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder={t('payouts_page.all_statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('payouts_page.all_statuses')}</SelectItem>
                <SelectItem value="GENERATED">{t('payouts_page.status_pending')}</SelectItem>
                <SelectItem value="PAID">{t('payouts_page.status_paid')}</SelectItem>
                <SelectItem value="PAID_OUT">{t('payouts_page.status_paid_out')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="payout-summary">
        <Card className="shadow-sm border-[#194a50]/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#194a50]/70">{t('payouts_page.total')}</p>
                <p className="text-2xl font-bold text-[#0c2431]">{formatCurrency(stats.total)}</p>
              </div>
              <Wallet className="w-8 h-8 text-[#194a50]/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">{t('payouts_page.status_pending')}</p>
                <p className="text-2xl font-bold text-amber-800">{formatCurrency(stats.pending)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">{t('payouts_page.paid_not_transferred')}</p>
                <p className="text-2xl font-bold text-blue-800">{formatCurrency(stats.paid)}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">{t('payouts_page.status_paid_out')}</p>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(stats.paidOut)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <Card className="shadow-sm border-[#194a50]/5" data-testid="invoices-table">
          <CardHeader>
            <CardTitle className="font-['Outfit'] flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {isOwner ? t('payouts_page.your_invoices') : t('payouts_page.owner_invoices')} ({invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-[#194a50]/60">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('payouts_page.no_invoices')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('payouts_page.col_invoice')}</th>
                      <th>{t('bookings.property')}</th>
                      {!isOwner && <th>{t('payouts_page.col_owner')}</th>}
                      <th>{t('payouts_page.col_booking')}</th>
                      <th>{t('payouts_page.col_dates')}</th>
                      <th>{t('payouts_page.col_amount')}</th>
                      <th>{t('common.status')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="font-medium text-[#0c2431]">{invoice.invoice_number}</td>
                        <td className="text-[#194a50]">{invoice.property_title}</td>
                        {!isOwner && <td className="text-[#194a50]">{invoice.owner_name}</td>}
                        <td>
                          <Link
                            to={`/bookings/${invoice.booking_id}`}
                            className="text-[#194a50] hover:text-[#10555e] hover:underline"
                          >
                            {invoice.booking_reference}
                          </Link>
                        </td>
                        <td className="text-sm text-[#194a50]/70">
                          {invoice.booking_dates?.check_in} - {invoice.booking_dates?.check_out}
                        </td>
                        <td className="font-semibold text-[#194a50]">
                          {formatCurrency(invoice.invoice_amount ?? invoice.total_payout)}
                          <div className="text-xs font-normal text-[#194a50]/60">
                            {t('payouts_page.payout_ref', { amount: formatCurrency(invoice.owner_payout ?? invoice.total_payout) })}
                          </div>
                        </td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadInvoice(invoice)}
                              data-testid={`download-invoice-${invoice.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>

                            {/* Admin status change buttons */}
                            {isAdmin && invoice.status === 'GENERATED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                                onClick={() => handleUpdateStatus(invoice.id, 'PAID')}
                                data-testid={`mark-paid-${invoice.id}`}
                              >
                                {t('payouts_page.mark_paid')}
                              </Button>
                            )}
                            {isAdmin && invoice.status === 'PAID' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500 text-green-700 hover:bg-green-50"
                                onClick={() => handleUpdateStatus(invoice.id, 'PAID_OUT')}
                                data-testid={`mark-paid-out-${invoice.id}`}
                              >
                                {t('payouts_page.mark_paid_out')}
                              </Button>
                            )}

                            {/* View booking link */}
                            <Link to={`/bookings/${invoice.booking_id}`}>
                              <Button size="sm" variant="ghost">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card for Owners */}
      {isOwner && (
        <Card className="shadow-sm border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">{t('payouts_page.status_guide_title')}</h4>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li><strong>{t('payouts_page.status_pending')}:</strong> {t('payouts_page.status_guide_pending')}</li>
                  <li><strong>{t('payouts_page.status_paid')}:</strong> {t('payouts_page.status_guide_paid')}</li>
                  <li><strong>{t('payouts_page.status_paid_out')}:</strong> {t('payouts_page.status_guide_paid_out')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PayoutsPage;
